import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import axios from "axios";
import Navbar from "@/components/Navbar";
import LemonBot from "@/components/LemonBot";
import LemonBotFloater from "@/components/LemonBotFloater";
import LogoGenerator from "@/components/LogoGenerator";
import VideoPlayer from "@/components/VideoPlayer";
import { triggerConfetti } from "@/utils/confetti";
import { playCompletionSound, playBadgeSound, playSubmitSound, playSessionCompleteSound } from "@/utils/sounds";
import { trackModuleCompleted } from "@/utils/analytics";
import LessonLabels from "@/components/LessonLabels";
import EducatorOverlay from "@/components/EducatorOverlay";
import CapstoneBuilder from "@/components/CapstoneBuilder";
import ModuleIntroPlaceholder from "@/components/ModuleIntroPlaceholder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to log activity to backend
async function logActivity(action, data = {}) {
  try {
    const token = localStorage.getItem("token") || localStorage.getItem("student_token");
    if (!token) return;
    await fetch(`${API}/admin/log-activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, ...data })
    });
  } catch (e) { /* silent */ }
}

function LessonView({ user, onLogout }) {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [module, setModule] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [submittedAnswers, setSubmittedAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({});
  const activityRef = useRef(null);
  const [questions, setQuestions] = useState([]);
  const [videoWatched, setVideoWatched] = useState(false);
  const [lessonVideoWatched, setLessonVideoWatched] = useState(false);
  const videoRef = useRef(null);
  const lessonVideoRef = useRef(null);
  const [showSessionCompleteModal, setShowSessionCompleteModal] = useState(false);
  const [sessionCompleteData, setSessionCompleteData] = useState(null);
  const [educatorMode, setEducatorMode] = useState(false);
  const isEducator = user?.role === "educator" || user?.role === "admin";

  useEffect(() => {
    loadModule();
  }, [lessonId]);

  // Auto-save effect
  // Get storage key scoped to user + lesson + question
  const getStorageKey = (lessonId, questionNumber) => {
    return `draft_${user.id}_${lessonId}_q${questionNumber}`;
  };

  // Parse activity instructions into questions
  const parseQuestions = (activityText) => {
    if (!activityText) return [];
    
    // Strategy 1: Look for inline numbered questions like "1) ... 2) ... 3) ..."
    // Split by the pattern that indicates start of a new question
    const inlinePattern = /(\d+[\).]\s+)/;
    const parts = activityText.split(inlinePattern);
    
    const parsed = [];
    let currentNumber = null;
    let currentText = "";
    
    parts.forEach((part) => {
      // Check if this part is a question number marker (e.g., "1) " or "2. ")
      const numberMatch = part.match(/^(\d+)[\).]\s+$/);
      if (numberMatch) {
        // Save previous question if exists
        if (currentNumber !== null && currentText.trim()) {
          parsed.push({
            number: currentNumber,
            text: currentText.trim()
          });
        }
        // Start new question
        currentNumber = parseInt(numberMatch[1]);
        currentText = "";
      } else {
        // Accumulate text for current question
        currentText += part;
      }
    });
    
    // Save last question
    if (currentNumber !== null && currentText.trim()) {
      parsed.push({
        number: currentNumber,
        text: currentText.trim()
      });
    }
    
    // If we found multiple questions using inline pattern, return them
    if (parsed.length > 1) return parsed;
    
    // Strategy 2: Try line-by-line parsing for newline-separated questions
    const lines = activityText.split('\n');
    const linesParsed = [];
    let currentQuestion = null;
    
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      
      // Check if line starts with a number
      const match = trimmed.match(/^(\d+)[\).]\s+(.*)/);
      if (match) {
        if (currentQuestion) {
          linesParsed.push(currentQuestion);
        }
        currentQuestion = {
          number: parseInt(match[1]),
          text: match[2].trim()
        };
      } else if (currentQuestion) {
        // Continue previous question
        currentQuestion.text += ' ' + trimmed;
      }
    });
    
    if (currentQuestion) {
      linesParsed.push(currentQuestion);
    }
    
    // Return whichever strategy found more questions
    if (linesParsed.length > parsed.length) {
      return linesParsed;
    }
    
    // If still no parsed questions, treat entire text as one question
    return parsed.length > 0 ? parsed : [{ number: 1, text: activityText }];
  };

  useEffect(() => {
    if (!currentLesson) return;

    // Parse questions from activity instructions
    const parsed = parseQuestions(currentLesson.activity_instructions);
    setQuestions(parsed);

    // Clear any old non-scoped keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('answer_')) {
        localStorage.removeItem(key);
      }
    });

    // Load answers for this lesson
    loadAnswers(currentLesson.id);

    return () => {
      // Cleanup on unmount
    };
  }, [currentLesson?.id]);

  const loadModule = async () => {
    try {
      // lessonId is actually module_id in this case
      const currentLang = i18n.language || 'en';
      const modulesRes = await axios.get(`${API}/modules?lang=${currentLang}`);
      setAllModules(modulesRes.data);
      const foundModule = modulesRes.data.find(m => m.id === lessonId);
      
      if (foundModule) {
        setModule(foundModule);
        const lessonsRes = await axios.get(`${API}/modules/${lessonId}/lessons?lang=${currentLang}`);
        setLessons(lessonsRes.data);
        if (lessonsRes.data.length > 0) {
          setCurrentLesson(lessonsRes.data[0]);
          loadAnswers(lessonsRes.data[0].id);
        }
        
        // Check if video has been watched
        if (foundModule.video_url) {
          try {
            const videoRes = await axios.get(`${API}/videos/${lessonId}/watched`);
            setVideoWatched(videoRes.data.watched);
          } catch (err) {
            console.error("Error checking video status:", err);
          }
        }
      }

      const progressRes = await axios.get(`${API}/progress/my`);
      setProgress(progressRes.data);
    } catch (err) {
      console.error("Error loading lesson:", err);
    } finally {
      setLoading(false);
    }
  };

  // Get the next module in sequence
  const getNextModule = () => {
    if (!module || !allModules.length) return null;
    const currentOrder = module.order || 0;
    return allModules.find(m => m.order === currentOrder + 1);
  };

  const handleGoToNextSession = () => {
    const nextModule = getNextModule();
    if (nextModule) {
      navigate(`/lessons/${nextModule.id}`);
    }
  };

  const areAllLessonsComplete = () => {
    if (!lessons.length) return false;
    // Check that every lesson in this module has a completed progress entry
    const completedLessonIds = progress
      .filter(p => p.completed === true)
      .map(p => p.lesson_id);
    return lessons.every(lesson => completedLessonIds.includes(lesson.id));
  };

  const shouldShowAssessment = () => {
    // Show assessment card for all 7 sessions when all lessons are complete
    if (!module || module.order > 7) return false;
    return areAllLessonsComplete();
  };

  const handleGoToAssessment = () => {
    navigate(`/assessment/${module.id}`);
  };

  const loadAnswers = async (lessonId) => {
    try {
      const response = await axios.get(`${API}/answers/lesson/${lessonId}`);
      setSubmittedAnswers(response.data);
      
      const newAnswers = {};
      const newUnsaved = {};
      
      // Load submitted answers
      response.data.forEach(ans => {
        const qNum = ans.question_number || 1;
        newAnswers[qNum] = ans.answer_text;
        newUnsaved[qNum] = false;
      });
      
      // Load drafts from localStorage for questions without submitted answers
      questions.forEach(q => {
        if (!newAnswers[q.number]) {
          const storageKey = getStorageKey(lessonId, q.number);
          const draft = localStorage.getItem(storageKey);
          if (draft) {
            newAnswers[q.number] = draft;
          }
        }
      });
      
      setAnswers(newAnswers);
      setHasUnsavedChanges(newUnsaved);
    } catch (err) {
      console.error("Error loading answers:", err);
    }
  };

  // Debounced auto-save for each question
  useEffect(() => {
    if (!currentLesson) return;

    const timeouts = {};
    
    Object.keys(hasUnsavedChanges).forEach(qNum => {
      if (hasUnsavedChanges[qNum] && answers[qNum]?.trim()) {
        timeouts[qNum] = setTimeout(() => {
          const storageKey = getStorageKey(currentLesson.id, qNum);
          localStorage.setItem(storageKey, answers[qNum]);
          setAutoSaveStatus(prev => ({ ...prev, [qNum]: "Draft saved" }));
          setTimeout(() => {
            setAutoSaveStatus(prev => ({ ...prev, [qNum]: "" }));
          }, 2000);
        }, 1000);
      }
    });

    return () => {
      Object.values(timeouts).forEach(t => clearTimeout(t));
    };
  }, [answers, currentLesson?.id, hasUnsavedChanges]);

  const handleAnswerChange = (questionNumber, value) => {
    setAnswers(prev => ({ ...prev, [questionNumber]: value }));
    setHasUnsavedChanges(prev => ({ ...prev, [questionNumber]: true }));
    setAutoSaveStatus(prev => ({ ...prev, [questionNumber]: "Typing..." }));
  };

  const handleSubmitAnswer = async (questionNumber) => {
    if (!answers[questionNumber]?.trim() || !currentLesson) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/answers`, {
        lesson_id: currentLesson.id,
        answer_text: answers[questionNumber].trim(),
        question_number: questionNumber
      });
      
      // Clear localStorage after successful submit
      const storageKey = getStorageKey(currentLesson.id, questionNumber);
      localStorage.removeItem(storageKey);
      
      setHasUnsavedChanges(prev => ({ ...prev, [questionNumber]: false }));
      await loadAnswers(currentLesson.id);
      
      // Play submit sound
      playSubmitSound();
      
      setAutoSaveStatus(prev => ({ ...prev, [questionNumber]: "Submitted! 🎉" }));
      setTimeout(() => {
        setAutoSaveStatus(prev => ({ ...prev, [questionNumber]: "" }));
      }, 3000);
    } catch (err) {
      console.error("Error submitting answer:", err);
      alert("Failed to submit answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLessonChange = (lesson) => {
    // Warn about unsaved changes
    const hasAnyUnsaved = Object.values(hasUnsavedChanges).some(v => v);
    const hasAnyText = Object.values(answers).some(v => v?.trim());
    
    if (hasAnyUnsaved && hasAnyText) {
      const confirmed = window.confirm("You have unsaved changes. Do you want to leave without saving?");
      if (!confirmed) return;
    }
    
    setCurrentLesson(lesson);
    setHasUnsavedChanges({});
    setAnswers({});
    loadAnswers(lesson.id);
  };

  // Warn before page unload with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const hasAnyUnsaved = Object.values(hasUnsavedChanges).some(v => v);
      const hasAnyText = Object.values(answers).some(v => v?.trim());
      
      if (hasAnyUnsaved && hasAnyText) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, answers]);

  const handleToggleComplete = async (lesson) => {
    const isCompleted = isLessonComplete(lesson.id);
    const action = isCompleted ? "unmark" : "mark";
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this lesson as ${isCompleted ? "incomplete" : "complete"}?`
    );
    
    if (!confirmed) return;
    
    try {
      // Get current badges before completing
      const userBefore = await axios.get(`${API}/auth/me`, { timeout: 5000 });
      const badgesBefore = userBefore.data.badges || [];
      
      await axios.post(`${API}/lessons/${lesson.id}/complete`, {}, { timeout: 5000 });
      
      // Get badges after completing
      const userAfter = await axios.get(`${API}/auth/me`, { timeout: 5000 });
      const badgesAfter = userAfter.data.badges || [];
      
      // Check if new badges were earned
      const newBadges = badgesAfter.filter(b => !badgesBefore.includes(b));
      
      // Trigger confetti and sounds if marking as complete or earned badges
      if (!isCompleted) {
        triggerConfetti();
        playCompletionSound();
      }
      
      if (newBadges.length > 0) {
        setTimeout(() => {
          playBadgeSound();
        }, 500);
      }
      
      await loadModule();
      
      // Check if this completion finished the entire session
      if (!isCompleted) {
        // Re-fetch progress to check if session is now complete
        const progressRes = await axios.get(`${API}/progress/my`);
        const updatedProgress = progressRes.data;
        const completedLessonIds = updatedProgress
          .filter(p => p.completed === true)
          .map(p => p.lesson_id);
        const allLessonsComplete = lessons.every(l => completedLessonIds.includes(l.id));
        
        if (allLessonsComplete && module) {
          trackModuleCompleted({ role: user?.role, moduleName: module.title, sessionNumber: module.order });
          logActivity("module_completed", { module_name: module.title, details: { session_number: module.order } });
          // Session complete! Show celebration modal
          const nextModule = allModules.find(m => m.order === (module.order || 0) + 1);
          setSessionCompleteData({
            sessionNumber: module.order,
            sessionTitle: module.title,
            lessonsCompleted: lessons.length,
            newBadges: newBadges,
            nextSession: nextModule
          });
          setShowSessionCompleteModal(true);
          
          // Play celebration sound and confetti
          playSessionCompleteSound();
          setTimeout(() => {
            triggerConfetti();
            triggerConfetti();
          }, 300);
        }
      }
    } catch (err) {
      console.error("Error toggling completion:", err);
      alert("Failed to update lesson status. Please try again.");
    }
  };

  const handleVideoWatched = async () => {
    if (!module || !module.video_url || videoWatched) return;
    
    try {
      await axios.post(`${API}/videos/${module.id}/mark-watched`);
      setVideoWatched(true);
    } catch (err) {
      console.error("Error marking video as watched:", err);
    }
  };

  const handleLessonVideoWatched = async () => {
    if (!currentLesson || !currentLesson.video_url || lessonVideoWatched) return;
    
    try {
      await axios.post(`${API}/videos/lesson/${currentLesson.id}/mark-watched`);
      setLessonVideoWatched(true);
    } catch (err) {
      console.error("Error marking lesson video as watched:", err);
    }
  };

  // Check if lesson video has been watched when lesson changes
  useEffect(() => {
    const checkLessonVideoStatus = async () => {
      if (currentLesson?.video_url) {
        try {
          const response = await axios.get(`${API}/videos/lesson/${currentLesson.id}/watched`);
          setLessonVideoWatched(response.data.watched);
        } catch (err) {
          setLessonVideoWatched(false);
        }
      } else {
        setLessonVideoWatched(false);
      }
    };
    checkLessonVideoStatus();
  }, [currentLesson?.id]);

  const scrollToActivity = () => {
    if (activityRef.current) {
      activityRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getNextLesson = () => {
    const currentIndex = lessons.findIndex(l => l.id === currentLesson?.id);
    if (currentIndex >= 0 && currentIndex < lessons.length - 1) {
      return lessons[currentIndex + 1];
    }
    return null;
  };

  const handleNextLesson = () => {
    const nextLesson = getNextLesson();
    if (nextLesson) {
      handleLessonChange(nextLesson);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isLessonComplete = (lessonId) => {
    return progress.some(p => p.lesson_id === lessonId && p.completed);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-xl text-gray-600">Module not found</p>
          <button onClick={() => navigate("/dashboard")} className="lemon-button px-6 py-3 rounded-lg text-white font-semibold mt-4">
            {t('lesson.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // Get translated session title
  const sessionTitle = t(`sessions.${module.order}.title`, { defaultValue: module.title });
  const sessionDesc = t(`sessions.${module.order}.description`, { defaultValue: module.description });

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-6 text-gray-600 hover:text-gray-800 font-semibold flex items-center gap-2"
          data-testid="back-to-dashboard-btn"
        >
          ← {t('lesson.backToDashboard')}
        </button>

        {/* Module Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="text-5xl">🍋</div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800" data-testid="module-title">{sessionTitle}</h1>
              <p className="text-gray-600">{sessionDesc}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-6">
            <div className="flex justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700">{t('lesson.moduleProgress')}</span>
              <span className="text-sm font-semibold text-gray-700">
                {progress.filter(p => lessons.some(l => l.id === p.lesson_id)).length} / {lessons.length} {t('lesson.lessonsCompleted')}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="progress-bar h-3 rounded-full"
                style={{ width: `${lessons.length > 0 ? (progress.filter(p => lessons.some(l => l.id === p.lesson_id)).length / lessons.length) * 100 : 0}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Lessons List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              {/* Educator Mode Toggle */}
              {isEducator && (
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100" data-testid="educator-toggle-container">
                  <span className="text-sm font-medium text-gray-600">Educator View</span>
                  <button
                    onClick={() => setEducatorMode(!educatorMode)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${educatorMode ? "bg-indigo-500" : "bg-gray-300"}`}
                    data-testid="educator-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${educatorMode ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              )}
              <h2 className="text-xl font-bold text-gray-800 mb-4">{t('dashboard.lessons')}</h2>
              <div className="space-y-3">
                {lessons.map((lesson, index) => (
                  <div
                    key={lesson.id}
                    className={`lesson-card p-4 rounded-lg cursor-pointer ${
                      currentLesson?.id === lesson.id ? "bg-yellow-50 border-yellow-400" : "bg-gray-50"
                    }`}
                    onClick={() => handleLessonChange(lesson)}
                    data-testid={`lesson-item-${lesson.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 ${
                        isLessonComplete(lesson.id) ? "text-green-600 text-2xl" : "text-gray-400 text-xl"
                      }`}>
                        {isLessonComplete(lesson.id) ? "✓" : index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800 text-sm">{t(`lessons.${lesson.title}`, { defaultValue: lesson.title })}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Assessment Card - Shows after all lessons complete */}
              {shouldShowAssessment() && (
                <div className="mt-4">
                  <div
                    onClick={handleGoToAssessment}
                    className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl shadow-lg hover:shadow-xl cursor-pointer transform hover:scale-105 transition-all"
                  >
                    <div className="flex items-center gap-3 text-white">
                      <div className="text-3xl">🎯</div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1">{t('lesson.sessionReflection')}</h3>
                        <p className="text-sm opacity-90">{t('lesson.completeReflection')}</p>
                      </div>
                      <div className="text-2xl">→</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Next Session Button - Shows after all lessons complete and there's a next session */}
              {areAllLessonsComplete() && getNextModule() && (
                <div className="mt-4">
                  <button
                    onClick={handleGoToNextSession}
                    className="w-full p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl shadow-lg hover:shadow-xl cursor-pointer transform hover:scale-105 transition-all"
                    data-testid="next-session-btn"
                  >
                    <div className="flex items-center gap-3 text-white">
                      <div className="text-3xl">🚀</div>
                      <div className="flex-1 text-left">
                        <h3 className="font-bold text-lg mb-1">{t('lesson.continueToNextSession', 'Continue to Next Session')}</h3>
                        <p className="text-sm opacity-90">
                          {t(`sessions.${getNextModule().order}.title`, { defaultValue: getNextModule().title })}
                        </p>
                      </div>
                      <div className="text-2xl">→</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Lesson Content */}
          <div className="lg:col-span-2">
            {currentLesson ? (
              <div>
                {/* Module Intro Video Placeholder */}
                <ModuleIntroPlaceholder module={module} />
                
                <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
                  {/* Lesson Labels */}
                  <LessonLabels module={module} />
                  
                  {/* Educator Overlay */}
                  {educatorMode && <EducatorOverlay module={module} />}
                  
                  <div className="flex items-start justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800" data-testid="lesson-title">{t(`lessons.${currentLesson.title}`, { defaultValue: currentLesson.title })}</h2>
                    <button
                      onClick={() => handleToggleComplete(currentLesson)}
                      className={`px-6 py-2 rounded-lg font-semibold ${
                        isLessonComplete(currentLesson.id)
                          ? "bg-green-100 text-green-700 border-2 border-green-400"
                          : "lemon-button text-white"
                      }`}
                      data-testid="toggle-complete-btn"
                      disabled={module?.video_required && !videoWatched}
                      title={module?.video_required && !videoWatched ? "Please watch the video first" : ""}
                    >
                      {isLessonComplete(currentLesson.id) ? `✓ ${t('lesson.completed')}` : t('lesson.markComplete')}
                    </button>
                  </div>

                  {/* Lesson Video - Show if lesson has video_url */}
                  {currentLesson?.video_url && (
                    <div className="mb-8">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-6">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="text-3xl">🎬</span>
                          <h3 className="text-xl font-bold text-gray-800">
                            {currentLesson.video_title || t('lesson.lessonVideo')}
                          </h3>
                          {lessonVideoWatched && (
                            <span className="ml-auto bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                              <span>✓</span> {t('lesson.watched')}
                            </span>
                          )}
                        </div>
                        
                        <VideoPlayer
                          key={currentLesson.id}
                          src={currentLesson.video_url}
                          videoRef={lessonVideoRef}
                          onEnded={handleLessonVideoWatched}
                          onTimeUpdate={(e) => {
                            // Mark as watched when 90% complete
                            const video = e.target;
                            if (video.duration && video.currentTime / video.duration >= 0.9) {
                              handleLessonVideoWatched();
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Module Video - Only show on first lesson */}
                  {/* Lesson Content */}
                  <div className="prose max-w-none mb-6">
                    <div className="whitespace-pre-wrap text-gray-700 leading-relaxed" data-testid="lesson-content">
                      {currentLesson.content}
                    </div>
                  </div>

                  {/* Start Activity Button */}
                  {currentLesson.activity_instructions && (
                    <div className="text-center mb-6">
                      <button
                        onClick={scrollToActivity}
                        className="lemon-button px-8 py-3 rounded-lg text-white font-semibold"
                        data-testid="start-activity-btn"
                      >
                        📝 {t('lesson.startActivity')}
                      </button>
                    </div>
                  )}

                  {/* Activity Instructions */}
                  {currentLesson.activity_instructions && questions.length > 0 && (
                    <div ref={activityRef} className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg mb-6">
                      <h3 className="text-lg font-bold text-blue-900 mb-4">🎯 {t('lesson.activity')}</h3>
                      
                      {/* Individual Questions */}
                      <div className="space-y-6">
                        {questions.map((question) => {
                          const submittedAnswer = submittedAnswers.find(a => a.question_number === question.number);
                          
                          return (
                            <div key={question.number} className="bg-white p-4 rounded-lg">
                              <h4 className="font-semibold text-blue-900 mb-3">
                                Question {question.number}: {question.text}
                              </h4>
                              
                              <textarea
                                value={answers[question.number] || ""}
                                onChange={(e) => handleAnswerChange(question.number, e.target.value)}
                                className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 min-h-[100px]"
                                placeholder="Type your answer here... (auto-saves as you type)"
                                data-testid={`answer-textarea-q${question.number}`}
                              />
                              
                              <div className="flex items-center justify-between mt-3">
                                <span className="text-sm text-gray-600">
                                  {autoSaveStatus[question.number] ? (
                                    <span className="text-green-600 font-semibold">{autoSaveStatus[question.number]}</span>
                                  ) : submittedAnswer ? (
                                    `Last submitted: ${new Date(submittedAnswer.submitted_at).toLocaleString()}`
                                  ) : hasUnsavedChanges[question.number] ? (
                                    <span className="text-orange-600">Unsaved changes</span>
                                  ) : (
                                    'Not submitted yet'
                                  )}
                                </span>
                                <button
                                  onClick={() => handleSubmitAnswer(question.number)}
                                  disabled={submitting || !answers[question.number]?.trim()}
                                  className="lemon-button px-6 py-2 rounded-lg text-white font-semibold disabled:opacity-50"
                                  data-testid={`submit-answer-btn-q${question.number}`}
                                >
                                  {submitting ? "Submitting..." : submittedAnswer ? "Update" : "Submit"}
                                </button>
                              </div>

                              {/* Teacher Feedback Display */}
                              {submittedAnswer?.feedback && (
                                <div className="mt-4 bg-green-50 border-2 border-green-300 rounded-lg p-4" data-testid={`teacher-feedback-q${question.number}`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">👩‍🏫</span>
                                    <h5 className="font-bold text-green-900">Teacher Feedback</h5>
                                    <span className="text-xs text-green-700 ml-auto">
                                      {new Date(submittedAnswer.feedback_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <p className="text-green-800 whitespace-pre-wrap">
                                    {submittedAnswer.feedback}
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Logo Generator - Show for "Your Business Idea" lesson (Session 1, Lesson 3) */}
                  {currentLesson?.title === "Your Business Idea" && (
                    <LogoGenerator 
                      studentAnswers={answers}
                      onLogoGenerated={(logo) => {
                        console.log("Logo generated:", logo);
                      }}
                    />
                  )}
                </div>

                {/* Capstone Builder - Build Your Business */}
                <CapstoneBuilder module={module} />

                {/* LemonBot with context-aware prompts */}
                <LemonBot lessonId={currentLesson.id} suggestedPrompts={module?.lemonbot_prompts} />

                {/* Next Lesson Button */}
                {getNextLesson() && (
                  <div className="mt-6 sticky bottom-4 z-10">
                    <button
                      onClick={handleNextLesson}
                      className="w-full lemon-button py-4 rounded-xl text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                      data-testid="next-lesson-btn"
                    >
                      {t('lesson.nextLesson')}: {t(`lessons.${getNextLesson().title}`, { defaultValue: getNextLesson().title })} →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="text-6xl mb-4">🍋</div>
                <p className="text-gray-600">{t('lesson.selectLesson')}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Session Complete Celebration Modal */}
      {showSessionCompleteModal && sessionCompleteData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="session-complete-modal">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-bounce-in">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500 p-8 text-center">
              <div className="text-7xl mb-4 animate-bounce">🎉</div>
              <h2 className="text-3xl font-bold text-white drop-shadow-lg">
                {t('lesson.sessionComplete', 'Session Complete!')}
              </h2>
              <p className="text-white/90 text-lg mt-2">
                {t(`sessions.${sessionCompleteData.sessionNumber}.title`, { defaultValue: sessionCompleteData.sessionTitle })}
              </p>
            </div>
            
            {/* Stats */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{sessionCompleteData.lessonsCompleted}</div>
                  <div className="text-sm text-green-700">{t('lesson.lessonsCompleted', 'Lessons Completed')}</div>
                </div>
                <div className="bg-yellow-50 rounded-xl p-4 text-center">
                  <div className="text-3xl">🏆</div>
                  <div className="text-sm text-yellow-700">{t('lesson.sessionMastered', 'Session Mastered!')}</div>
                </div>
              </div>
              
              {/* New badges earned */}
              {sessionCompleteData.newBadges?.length > 0 && (
                <div className="bg-purple-50 rounded-xl p-4 mb-6 text-center">
                  <div className="text-sm font-semibold text-purple-700 mb-2">
                    {t('lesson.newBadgesEarned', 'New Badges Earned!')}
                  </div>
                  <div className="flex justify-center gap-2">
                    {sessionCompleteData.newBadges.map((badge, i) => (
                      <span key={i} className="text-2xl">🏅</span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="space-y-3">
                {/* Session Reflection */}
                <button
                  onClick={() => {
                    setShowSessionCompleteModal(false);
                    handleGoToAssessment();
                  }}
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-3"
                  data-testid="modal-assessment-btn"
                >
                  <span className="text-2xl">🎯</span>
                  {t('lesson.completeReflection', 'Complete Session Reflection')}
                </button>
                
                {/* Next Session (if available) */}
                {sessionCompleteData.nextSession && (
                  <button
                    onClick={() => {
                      setShowSessionCompleteModal(false);
                      handleGoToNextSession();
                    }}
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all flex items-center justify-center gap-3"
                    data-testid="modal-next-session-btn"
                  >
                    <span className="text-2xl">🚀</span>
                    {t('lesson.continueToNextSession', 'Continue to Next Session')}
                  </button>
                )}
                
                {/* Stay here */}
                <button
                  onClick={() => setShowSessionCompleteModal(false)}
                  className="w-full py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                  data-testid="modal-close-btn"
                >
                  {t('lesson.stayHere', 'Stay on this page')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LemonBotFloater
        lessonId={currentLesson?.id}
        moduleId={module?.id}
        lessonTitle={currentLesson?.title}
        moduleTitle={module?.title}
        activity="standard_lesson"
      />
    </div>
  );
}

export default LessonView;
